/**
 * CompareToControls — the metric branch of a rule row in COMPARE mode:
 *
 *   [ % (opt-in) ] [ RHS metric ▼ ] over [ same window | window ▼ ]   [#]
 *
 * Replaces the fixed-value ThresholdInput while `node.compareTo` is set. The
 * DEFAULT is the direct comparison — no percent stored; PercentToggle opts
 * into the tolerance and hovers the live calculation. The window defaults to
 * "same window" (the group's own range); naming one reuses the exact dropdown
 * the group window uses. The [#] button returns to a fixed value — the old
 * `value` was never touched, so nothing is lost.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { Hash, X } from 'lucide-react';
import type { MetricId, RuleNode } from '@trafficpulse/shared';

import { getStatsDateRangeLabel } from '@/components/date-range';
import type { StatsDateRangePreset } from '@/components/date-range';

import FormulaGroupDateRangeDropdown from '../../calculation/group/date-range/FormulaGroupDateRangeDropdown';

import PercentToggle from './compare/PercentToggle';
import { buildMetricOptions } from './metric-options';

type MetricNode = Extract<RuleNode, { kind: 'condition' }>;
type MetricMeta = Parameters<typeof buildMetricOptions>[0];

export interface CompareToControlsProps {
  node: MetricNode;
  metrics: MetricMeta;
  /** Hover text for the active % control — the spelled-out calculation. */
  calcTitle: string;
  /** Hover text for the inactive % button. */
  addTitle: string;
  onChange: (next: RuleNode) => void;
}

/** Friendly window name for the live sentence; undefined = the group's own. */
export function compareWindowLabelOf(ct: {
  dateRange?: string;
  dateRangeCustomFrom?: string;
  dateRangeCustomTo?: string;
}): string | undefined {
  if (!ct.dateRange) return undefined;
  if (ct.dateRange !== 'custom') {
    return getStatsDateRangeLabel(ct.dateRange as StatsDateRangePreset);
  }
  return `${ct.dateRangeCustomFrom ?? '?'} → ${ct.dateRangeCustomTo ?? 'today'}`;
}

const CompareToControls: FC<CompareToControlsProps> = ({
  node,
  metrics,
  calcTitle,
  addTitle,
  onChange,
}) => {
  const ct = node.compareTo ?? { metric: node.metric };
  const patch = (next: Partial<typeof ct>) =>
    onChange({ ...node, compareTo: { ...ct, ...next } });

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <PercentToggle
        percent={ct.percent}
        calcTitle={calcTitle}
        addTitle={addTitle}
        onChange={(percent) => patch({ percent })}
      />
      <select
        value={ct.metric}
        onChange={(e) => patch({ metric: e.target.value as MetricId })}
        aria-label="Compare against metric"
        className="h-8 w-32 shrink-0 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {buildMetricOptions(metrics).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <select
        value={ct.against === 'total' ? 'total' : 'self'}
        onChange={(e) =>
          patch({ against: e.target.value === 'total' ? 'total' : undefined })
        }
        aria-label="Compare against"
        title="this element = its own numbers. TOTAL = the blended total of the whole layer (the TOTAL row) for that window."
        className="h-8 w-24 shrink-0 rounded-md border border-input bg-background px-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      >
        <option value="self">this element</option>
        <option value="total">TOTAL</option>
      </select>
      {ct.dateRange ? (
        <span className="flex shrink-0 items-center gap-0.5">
          <FormulaGroupDateRangeDropdown
            dateRange={ct.dateRange}
            dateRangeCustomFrom={ct.dateRangeCustomFrom}
            dateRangeCustomTo={ct.dateRangeCustomTo}
            onCommit={(p) =>
              patch({
                dateRange: p.dateRange,
                dateRangeCustomFrom: p.dateRangeCustomFrom,
                dateRangeCustomTo: p.dateRangeCustomTo,
              })
            }
          />
          <button
            type="button"
            onClick={() =>
              patch({
                dateRange: undefined,
                dateRangeCustomFrom: undefined,
                dateRangeCustomTo: undefined,
              })
            }
            title="Back to the rule's own window"
            aria-label="Clear compare window"
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => patch({ dateRange: 'last-30-days' })}
          title="The comparison reads the rule's own window — click to give it a window of its own"
          className="h-8 shrink-0 rounded-md border border-dashed border-border px-2 text-[11px] text-muted-foreground transition-colors hover:border-primary/45 hover:text-foreground"
        >
          same window
        </button>
      )}
      <button
        type="button"
        onClick={() => onChange({ ...node, compareTo: undefined })}
        title="Use a fixed value instead"
        aria-label="Use a fixed value instead"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <Hash className="h-3.5 w-3.5" />
      </button>
    </span>
  );
};

export default CompareToControls;
